import { ChangeEvent, FormEvent, useState } from 'react';
import './ContactSection.css';

type ContactFormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const initialState: ContactFormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
};

const CONTACT_ENDPOINT = '/backend/contact_mail.php';

function ContactSection() {
  const [formData, setFormData] = useState<ContactFormData>(initialState);
  const [status, setStatus] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const { name, email, subject, message } = formData;

    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus('Merci de remplir le nom, l’email et le message.');
      return;
    }

    setIsSubmitting(true);
    setStatus('Envoi du message...');

    try {
      const payload = new URLSearchParams({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });

      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json, text/plain, */*',
        },
        body: payload.toString(),
      });

      const rawResponse = await response.text();
      const normalizedResponse = rawResponse.trim().toLowerCase();

      if (!response.ok) {
        throw new Error(rawResponse || 'Erreur serveur');
      }

      if (
        normalizedResponse &&
        ['error', 'erreur', 'false', '0', 'ko'].includes(normalizedResponse)
      ) {
        throw new Error(rawResponse || 'Le backend a retourné une erreur.');
      }

      setStatus('Message envoyé avec succès.');
      setFormData(initialState);
    } catch (error) {
      console.error('Erreur lors de l’envoi du formulaire :', error);
      setStatus('Impossible d’envoyer le message pour le moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="contact__layout">
        <article className="contact__info section-card">
          <div className="section-heading">
            <span className="section-eyebrow">Contact</span>
            <h2 className="section-title">Parlons de votre projet</h2>
            <p className="section-copy">
              Besoin d’un développeur rigoureux, polyvalent et sensible à la qualité des interfaces ?
              Je suis disponible pour échanger autour d’un besoin logiciel, web ou data.
            </p>
          </div>

          <div className="contact__details">
             <a 
              className="contact__detail" 
              href="./documents/CV_Thomas_Amand.pdf"
              download
            >
              <span>Curriculum vitae</span>
              <strong>Télécharger</strong>
            </a>
            <a
              className="contact__detail"
              href="https://www.linkedin.com/in/thomas-amand-8bb9a3238/"
              target="_blank"
              rel="noreferrer"
            >
              <span>LinkedIn</span>
              <strong>Voir le profil</strong>
            </a>
           
          </div>
        </article>

        <form className="contact__form section-card" onSubmit={handleSubmit}>
          <div className="contact__field-grid">
            <label>
              <span>Nom</span>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Votre nom"
                autoComplete="name"
              />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="vous@exemple.com"
                autoComplete="email"
              />
            </label>
          </div>

          <label>
            <span>Sujet</span>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Objet du message"
            />
          </label>

          <label>
            <span>Message</span>
            <textarea
              name="message"
              rows={6}
              value={formData.message}
              onChange={handleChange}
              placeholder="Décrivez votre besoin"
            />
          </label>

          <div className="contact__actions">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
            </button>
          </div>
          {status ? <p className="contact__status">{status}</p> : null}
        </form>
      </div>
    </section>
  );
}

export default ContactSection;
